import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { Component, ElementRef, ChangeDetectorRef, ViewChild } from "@angular/core";
import {
  BmarkFixingData,
  CoreBenchmarkAudit,
  ProfilePromiseActionService,
  SystemConfigPromiseService,
  TableDefinition,
} from "shared/cubiq-services";
import { Subject, Subscription, debounceTime } from "rxjs";
import * as OrderAuditHelper from "shared/cubiq-services/model/audit/OrderAuditHelper";
import { GoldenLayoutComponent } from "app/golden-layout/golden-layout-component";
import { TranslateService } from "@ngx-translate/core";

import { BfxBaseBlotter, DEFAULT_ROW_HEIGHT } from "app/bfx-base-blotter/bfx-base-blotter";
import { BfxBaseBlotterColumnSelectorService } from "app/bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxSaveProfileNotificationService } from "app/bfx-common/services/bfx-save-profile-notification.service";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";
import { BMTimeCodeImpl, BlotterType } from "shared/cubiq-model-multi-asset";
import {
  BM_AUDIT_PROFILE_KEY_SUFFIX,
  FixingAuditTableDefinitionFactory,
} from "shared/cubiq-services/model/FixingAuditTableDefinitionFactory";
import { GoldenLayoutService } from "app/golden-layout/golden-layout-service";
import { BfxBlotterFilterService } from "app/bfx-blotter-filter/bfx-blotter-filter.service";

const END_OF_LINE = "\r\n";
const CSV_SEP = ",";
const AUDIT_TITLES: Array<string> = ["Date", "Action", "Comments", "User ID", "Order Status"];

import { LoggerService } from "@bfx/logger";
import {
  BmarkFixingsManagerService,
  FixingActiveTab,
  FixingTabType,
} from "app/bfx-bmark-summary/services/bmark-fixings-manager.service";
import {
  BenchmarkAuditLookupService,
  BenchmarkAuditUpdateEvent,
} from "shared/cubiq-services/lookup/model/impl/BenchmarkAuditLookupService";
import { BfxClipboardService } from "shared/helper-services/bfx-clipboard.service";
const logger = LoggerService.for(__dirname);

@Component({
  selector: "bfx-fixing-blotter-audit",
  templateUrl: "./html/bfx-fixing-blotter-audit.html",
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BfxFixingBlotterAuditComponent
  extends BfxBaseBlotter
  implements GoldenLayoutComponent
{
  public fixingAuditRecords: CoreBenchmarkAudit[] = [];

  public tableDefinition: TableDefinition;

  /** DIV in tempate used for measuring comment dimensions */
  @ViewChild("measureDiv") public measureDiv: ElementRef;

  public frameworkComponents: any = {};

  public refreshRowHeights: Subject<void> = new Subject();

  /** Last text node added to Measure div for measuring */
  private textNode: Text;

  private displayFixingSubscription: Subscription;
  private activeTabSubscription: Subscription;
  private auditUpdateSubscription: Subscription;

  private fixingId: BMTimeCodeImpl;

  private isVisible: boolean = false;

  constructor(
    element: ElementRef,
    _tableDefinitionFactory: FixingAuditTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    private systemConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
    protected _clipboardService: BfxClipboardService,
    protected cd: ChangeDetectorRef,
    private bmarkManager: BmarkFixingsManagerService,
    private auditService: BenchmarkAuditLookupService,
  ) {
    super(
      element,
      null,
      null,
      null,
      null,
      null,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      null,
      columnSelectorService,
      profileActionService,
      systemConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
    );
    this.blotterId = this.constructor.name;
    // Wait until col resizing has paused for X ms then resize all rows
    this.refreshRowHeights.pipe(debounceTime(200)).subscribe(() => {
      this.gridOptions?.api?.resetRowHeights();
    });
  }

  /**
   * Overriding to remove callback for cntext menu opening
   */
  protected initializeColumnDefinitions(): void {
    super.initializeColumnDefinitions();

    this.gridOptions.onCellContextMenu = null;

    this.gridOptions.api.sizeColumnsToFit();
    this.gridOptions.getRowHeight = (params) => {
      const audit: CoreBenchmarkAudit = params.data;

      const fullHeight = this.measureCommentText(audit.commentsDisplay);
      return fullHeight;
    };
    this.gridOptions.suppressAutoSize = false;

    this.gridOptions.onColumnResized = () => this.refreshRowHeights.next();
    this.gridOptions.onGridSizeChanged = () => {
      try {
        this._agGridResetScroll();
      } catch (e) {
        logger.out.warn(
          `${this.constructor.name}.onGridSizeChanged ag-grid private API use failed.`,
        );
      } finally {
        this.gridOptions?.api?.sizeColumnsToFit();
      }
    };
  }

  public override getBlotterTypeStr(): string {
    return BM_AUDIT_PROFILE_KEY_SUFFIX;
  }

  public override getBlotterType(): BlotterType {
    return BlotterType.OPSINTERVENTION;
  }

  protected getBlotterVersion(): string {
    return this._tableDefinitionFactory.getVersionIdentifier();
  }

  protected applyColumnDefinitions() {
    super.applyColumnDefinitions();

    this.cd.detectChanges();
  }

  /**********************************************************************/
  /* INITIALIZE */
  public ngOnInit() {
    this.initializeManager();
  }

  public ngOnDestroy() {
    this.displayFixingSubscription.unsubscribe();
    this.activeTabSubscription.unsubscribe();
    this.auditUpdateSubscription.unsubscribe();
  }

  public saveConfiguration(): void {
    let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];

    profileSettings.push(
      this.userProfileService.createProfileSettingObject(
        this.getProfileSettingKey(),
        JSON.stringify(this.getBMAuditConfiguration()),
      ),
    );
    this.profileActionService.updateProfile(profileSettings, false);
  }

  protected initializeManager(): void {
    this.activeTabSubscription = this.bmarkManager.activeTab$.subscribe(
      (activeTab: FixingActiveTab) => {
        this.isVisible = activeTab?.activeTabType === FixingTabType.FIXING_AUDIT;
        this.load();
      },
    );

    this.displayFixingSubscription = this.bmarkManager.displayFixing$.subscribe(
      (fixingData: BmarkFixingData) => {
        if (fixingData instanceof BmarkFixingData) {
          this.fixingId = fixingData.fixingId;
          logger.out.info(`Audit View: DISPLAY`);
        } else {
          this.fixingId = null;
        }
        this.load();
      },
    );

    this.auditUpdateSubscription = this.auditService.auditUpdate$.subscribe(
      (auditUpdate: BenchmarkAuditUpdateEvent) => {
        this.processAuditUpdate(auditUpdate);
      },
    );
  }
  processAuditUpdate(_auditUpdate: BenchmarkAuditUpdateEvent) {
    logger.out.info("Updating displayed audit log");
    if (this.fixingId && this.isVisible) {
      this.gridOptions.api.redrawRows();
    }
  }

  protected override initializeInMemory() {
    // Don't call parent
    this.load();
  }

  private async load() {
    if (!this.isVisible) {
      return;
    }
    if (this.fixingId == null) {
      this.gridOptions?.api?.setRowData([]);
      return;
    }

    this.fixingAuditRecords = [];
    // Keep local copy as async operation may change it
    let fixingId = this.fixingId;
    if (this.fixingId !== null) {
      this.fixingAuditRecords = await this.auditService.getAuditDataForFixing(this.fixingId);
    }
    if (fixingId === this.fixingId) {
      this.gridOptions?.api?.setRowData(this.fixingAuditRecords);
    }
  }

  /**
   * Invoked everytime a checkbox for the filter changes
   */
  reload() {
    this.load();
  }

  buildStringToCopy(audits: Array<CoreBenchmarkAudit>): string {
    let returnString = "";

    returnString += JSON.stringify(AUDIT_TITLES).replace(/[\[\]]/g, "") + CSV_SEP + END_OF_LINE;
    audits.forEach((element) => {
      let propsToDisplay: Array<string> = [];
      propsToDisplay.push(element.actionTimeStr);
      propsToDisplay.push(element.orderAction);
      propsToDisplay.push(OrderAuditHelper.getCommentsExport(element));
      propsToDisplay.push(element.userStr);
      propsToDisplay.push(element.orderStatus);

      returnString +=
        JSON.stringify(propsToDisplay)
          .replace(/[\[\]]/g, "")
          .replace(/\\/g, "") +
        CSV_SEP +
        END_OF_LINE;
    });

    return returnString;
  }

  public actionExport_copyToClipboard(): void {
    const auditToCopy = this.buildStringToCopy(this.fixingAuditRecords);
    try {
      this._clipboardService.setContent(auditToCopy).then(() => {
        if (!this._clipboardService.copyContent()) {
          logger.out.error(
            "ERROR: BfxOrderRecapComponent.actionExport_copyToClipboard(): Copy to clipboard operation failed!",
          );
        }
      });
    } catch (ex) {
      logger.out.error(
        "ERROR: BfxOrderRecapComponent.actionExport_copyToClipboard(): Exception: ",
        ex,
      );
    }
  }

  /**
   *
   * @param comment using the off-screen div, set the width to our column, then add a text
   * node with our comment and measure the height
   * @returns
   */
  public measureCommentText(comment: string): number {
    const col = this.gridColumnApi.getColumn("commentsDisplay");
    const colWidth = col ? col.getActualWidth() : 100;

    let retHeight = 0;
    if (this.measureDiv) {
      const measureDivElement = this.measureDiv.nativeElement as HTMLDivElement;
      if (this.textNode) {
        measureDivElement.removeChild(this.textNode);
      }
      measureDivElement.style.width = colWidth + "px";
      this.textNode = document.createTextNode(comment);
      measureDivElement.appendChild(this.textNode);
      retHeight = measureDivElement.clientHeight;
    }
    return Math.max(retHeight, DEFAULT_ROW_HEIGHT);
  }

  public getBMAuditConfiguration(): any {
    return this.getComponentConfiguration();
  }

  public restoreFromConfiguration(config: any): void {
    super.restoreFromConfiguration(config);
  }
}
